/**
 * Générateur — Automatisme : lire l'heure (cadran à aiguilles et digital)
 * Attendu ID : 1031 | Niveau : 6ème | Domaine : GM
 * Rubrique : AUTOMATISME (programme 2025) — « L'élève lit l'heure sur un
 * cadran à aiguilles ou sur un affichage digital (heures, minutes et
 * secondes). »
 * Labels (cartes exerciseur) :
 *   - Lire l'heure (cadran et digital)
 *
 * ── Plan de cartes (2026-08-22, cadrage prof) ──────────────────────────────
 * PÉRIMÈTRE STRICT : LIRE une heure affichée, rien d'autre. Placer les
 * aiguilles → 1032, relations entre unités → 1033, demi/quart d'heure →
 * 1035. L'ERREUR DOMINANTE : la petite aiguille proche du chiffre suivant
 * (3 h 50 lu « 4 h 50 ») — ~60 % des cadrans tirent min ∈ [35 ; 55], et la
 * petite aiguille est TOUJOURS décalée proportionnellement aux minutes.
 * Quatre formats : QCM cadran (erreurs nommées), saisie cadran (trotteuse
 * ~30 %), QCM digital 24 h ↔ lecture courante (matin/après-midi/soir, deux
 * sens), V/F digital + connaissance de l'instrument (l'aiguille courte...).
 * Minutes et secondes MULTIPLES DE 5 (décision prof : la lecture fine des
 * graduations n'est pas l'objet).
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou,
} from '../../../utils';

const ATTENDU_ID = 1031;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L1 = 'Lire l\'heure (cadran et digital)';

const garde = (condition, message) => {
    if (!condition) throw new Error(`gen_1031 : ${message}`);
};

const pad2 = (n) => String(n).padStart(2, '0');
const dataUrl = (svg) => `data:image/svg+xml,${encodeURIComponent(svg)}`;
const elImage = (url) => ({ version: 1, elements: [{ type: 'image', config: { imageUrl: url } }] });

// ─── La figure : cadran à aiguilles ───────────────────────────────────────

const BLEU = '#1E3A8A';

/** Point sur le cadran (angle en degrés depuis 12 h, sens horaire). */
const surCadran = (angle, rayon) => {
    const rad = (angle * Math.PI) / 180;
    return [120 + rayon * Math.sin(rad), 120 - rayon * Math.cos(rad)];
};

/**
 * Cadran 240×240, tout inclus dans le viewBox (chiffres compris).
 * La petite aiguille est décalée de min × 0,5° — C'EST l'objet de
 * l'automatisme (gardé par un throw).
 */
const svgCadran = (h, min, sec = null) => {
    garde(h >= 1 && h <= 12 && min >= 0 && min < 60 && min % 5 === 0, `heure invalide ${h}:${min}`);
    const angH = (h % 12) * 30 + min * 0.5;
    const angM = min * 6;
    garde(min === 0 || angH % 30 !== 0, 'petite aiguille pile sur un chiffre avec min non nul');
    const p = [];
    p.push('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240" width="200" height="200" role="img"');
    const versLe = min >= 35 ? `, proche du ${h % 12 + 1}` : '';
    const descSec = sec === null ? '' : ` La trotteuse rouge pointe le ${sec === 0 ? 12 : sec / 5}.`;
    p.push(`  aria-label="Cadran d'horloge. La petite aiguille est ${min === 0 ? `sur le ${h}` : `entre le ${h} et le ${h % 12 + 1}${versLe}`}. La grande aiguille pointe le ${min === 0 ? 12 : min / 5}.${descSec}">`);
    p.push(`  <circle cx="120" cy="120" r="112" fill="#eff6ff" stroke="${BLEU}" stroke-width="4"/>`);
    // graduations : 60 traits, les multiples de 5 plus marqués
    for (let i = 0; i < 60; i++) {
        const majeur = i % 5 === 0;
        const [x1, y1] = surCadran(i * 6, majeur ? 96 : 101);
        const [x2, y2] = surCadran(i * 6, 106);
        p.push(`  <line x1="${x1.toFixed(1)}" y1="${y1.toFixed(1)}" x2="${x2.toFixed(1)}" y2="${y2.toFixed(1)}" stroke="${majeur ? '#64748b' : '#94a3b8'}" stroke-width="${majeur ? 2.5 : 1}"/>`);
    }
    // chiffres 1..12
    for (let i = 1; i <= 12; i++) {
        const [x, y] = surCadran(i * 30, 80);
        p.push(`  <text x="${x.toFixed(1)}" y="${(y + 6).toFixed(1)}" font-size="17" font-weight="700" fill="${BLEU}" text-anchor="middle" font-family="system-ui,sans-serif">${i}</text>`);
    }
    // aiguilles effilées en losange, terminées en POINTE (demande prof
    // 2026-08-22) ; l'attribut data-aiguille sert au solveur test-side
    p.push(`  <polygon points="115.5,122 120,62 124.5,122 120,134" fill="${BLEU}" data-aiguille="heures" transform="rotate(${angH} 120 120)"/>`);
    p.push(`  <polygon points="117.2,122 120,32 122.8,122 120,134" fill="${BLEU}" data-aiguille="minutes" transform="rotate(${angM} 120 120)"/>`);
    if (sec !== null) {
        garde(sec >= 0 && sec < 60 && sec % 5 === 0, `secondes invalides ${sec}`);
        p.push(`  <polygon points="119,124 120,26 121,124 120,142" fill="#dc2626" data-aiguille="secondes" transform="rotate(${sec * 6} 120 120)"/>`);
    }
    p.push(`  <circle cx="120" cy="120" r="5.5" fill="${BLEU}"/>`);
    p.push('</svg>');
    return p.join('\n');
};

/** Affichage digital HH:MM (ou HH:MM:SS). */
const svgDigital = (h24, min, sec = null) => {
    const texte = sec === null ? `${pad2(h24)}:${pad2(min)}` : `${pad2(h24)}:${pad2(min)}:${pad2(sec)}`;
    return [
        `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 220 76" width="190" height="66" role="img" aria-label="Affichage digital : ${texte}">`,
        `  <rect x="4" y="4" width="212" height="68" rx="10" fill="#111827" stroke="${BLEU}" stroke-width="3"/>`,
        `  <text x="110" y="52" font-size="${sec === null ? 34 : 27}" font-weight="700" fill="#22d3ee" text-anchor="middle" font-family="Consolas,monospace" data-digital="${texte}">${texte}</text>`,
        '</svg>',
    ].join('\n');
};

// ─── Tirage d'une heure de cadran (le piège en majorité) ──────────────────

const tirerHeureCadran = () => {
    // h ∈ [1 ; 11] : les cas midi/minuit (12) sont écartés du cadran
    const h = randInt(1, 11);
    // ~60 % dans la zone du piège (petite aiguille proche du chiffre suivant)
    const min = Math.random() < 0.6
        ? randomChoice([35, 40, 45, 50, 55])
        : randomChoice([0, 5, 10, 15, 20, 25, 30]);
    // le MOMENT est donné dans l'énoncé (demande prof 2026-08-22) : le même
    // cadran se lit 3 h 50 le matin et 15 h 50 l'après-midi
    const pm = Math.random() < 0.5;
    const moment = pm ? (h <= 5 ? 'l\'après-midi' : 'le soir') : 'le matin';
    return { h, min, pm, moment, h24: pm ? h + 12 : h };
};

const fmtHeure = (h, min) => `${h} h ${pad2(min)}`;

// ─── Format 1 : QCM cadran (erreurs nommées) ──────────────────────────────

const genQcmCadran = () => {
    for (let essai = 0; essai < 40; essai++) {
        const { h, min, pm, moment, h24 } = tirerHeureCadran();
        const bonne = fmtHeure(h24, min);
        const chiffreMin = min === 0 ? 12 : min / 5;
        const decal = pm ? 12 : 0;
        const candidats = [
            // L'ERREUR CIBLE : lire l'heure qui approche au lieu de l'heure commencée
            { valeur: fmtHeure(h24 + 1, min), code: 'heure_suivante' },
            // le moment ignoré (3 h 50 l'après-midi) ou ajouté à tort (15 h 50 le matin)
            { valeur: fmtHeure(pm ? h : h + 12, min), code: 'mauvais_moment' },
            // les aiguilles échangées : l'heure lue sur la grande aiguille
            { valeur: fmtHeure(chiffreMin + (chiffreMin <= 11 ? decal : 0), h * 5), code: 'aiguilles_echangees' },
            // la minute lue comme le chiffre pointé (grande aiguille sur le 10 → « 10 min »)
            { valeur: fmtHeure(h24, chiffreMin), code: 'chiffre_brut' },
            // repli : l'heure d'avant (l'erreur symétrique)
            { valeur: fmtHeure(h24 - 1, min), code: 'heure_precedente' },
        ];
        const vus = new Set([bonne]);
        const dis = candidats.filter((c) => {
            const hh = parseInt(c.valeur, 10);
            if (hh < 1 || hh > 23 || vus.has(c.valeur)) return false;
            vus.add(c.valeur);
            return true;
        }).slice(0, 3);
        if (dis.length < 3) continue;
        const payload = makeQcm(
            [bonne, ...dis.map((c) => c.valeur)],
            bonne,
            `<p><b>C'est ${moment}. Quelle heure est-il sur ce cadran ?</b></p><p>{{element:0}}</p>`,
            { ...opts(L1), elements_additionnels: elImage(dataUrl(svgCadran(h, min))) },
            false,
        );
        payload._meta.params = { h, min, pm, distracteurs: dis };
        return payload;
    }
    // repli déterministe (jamais atteint en pratique)
    const payload = makeQcm(
        ['3 h 50', '4 h 50', '15 h 50', '3 h 10'],
        '3 h 50',
        '<p><b>C\'est le matin. Quelle heure est-il sur ce cadran ?</b></p><p>{{element:0}}</p>',
        { ...opts(L1), elements_additionnels: elImage(dataUrl(svgCadran(3, 50))) },
        false,
    );
    payload._meta.params = { h: 3, min: 50, pm: false, distracteurs: [] };
    return payload;
};

// ─── Format 2 : saisie cadran (trotteuse ~30 %) ───────────────────────────

const genSaisieCadran = () => {
    const { h, min: minBrut, moment, h24 } = tirerHeureCadran();
    // saisie : minutes ≥ 10 (éviter l'ambiguïté « 5 » / « 05 » au clavier)
    const min = minBrut < 10 ? minBrut + 10 : minBrut;
    const avecTrotteuse = Math.random() < 0.3;
    if (avecTrotteuse) {
        const sec = randomChoice([10, 15, 20, 25, 30, 35, 40, 45, 50, 55]);
        return makeTexteTrou(
            `Il est {{${h24}}} h {{${min}}} min {{${sec}}} s`,
            `<p><b>C'est ${moment}. Lis l'heure sur ce cadran (avec la trotteuse rouge pour les secondes).</b></p><p>{{element:0}}</p>`,
            { ...opts(L1), mode: 'saisie', elements_additionnels: elImage(dataUrl(svgCadran(h, min, sec))) },
        );
    }
    return makeTexteTrou(
        `Il est {{${h24}}} h {{${min}}}`,
        `<p><b>C'est ${moment}. Lis l'heure sur ce cadran.</b></p><p>{{element:0}}</p>`,
        { ...opts(L1), mode: 'saisie', elements_additionnels: elImage(dataUrl(svgCadran(h, min))) },
    );
};

// ─── Format 3 : QCM digital 24 h ↔ lecture courante ───────────────────────

/** Moment de la journée d'une heure 24 h (après-midi dès 12 h, soir dès 18 h). */
const moment = (h24) => (h24 >= 18 ? 'du soir' : 'de l\'après-midi');

const genQcmDigital = () => {
    for (let essai = 0; essai < 40; essai++) {
        const h24 = randInt(13, 23);
        const min = randomChoice([0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55]);
        const h12 = h24 - 12;
        const mo = moment(h24);
        if (Math.random() < 2 / 3) {
            // sens direct : l'affichage → la lecture courante
            const bonne = `${h12} h ${pad2(min)} ${mo}`;
            const candidats = [
                { valeur: `${h12} h ${pad2(min)} du matin`, code: 'matin_apres_midi' },
                { valeur: `${h24 % 10} h ${pad2(min)} ${mo}`, code: 'chiffre_des_unites' },
                { valeur: `${h24} h ${pad2(min)} du matin`, code: 'vingt_quatre_du_matin' },
                { valeur: `${h12 % 12 + 1} h ${pad2(min)} ${mo}`, code: 'heure_voisine' },
            ];
            const vus = new Set([bonne]);
            const dis = candidats.filter((c) => {
                if (vus.has(c.valeur) || c.valeur.startsWith('0 h')) return false;
                vus.add(c.valeur);
                return true;
            }).slice(0, 3);
            if (dis.length < 3) continue;
            const payload = makeQcm(
                [bonne, ...dis.map((c) => c.valeur)],
                bonne,
                '<p><b>Comment dit-on l\'heure affichée ?</b></p><p>{{element:0}}</p>',
                { ...opts(L1), elements_additionnels: elImage(dataUrl(svgDigital(h24, min))) },
                false,
            );
            payload.contenu = { ...payload.contenu, sousType: 'digital_direct' };
            payload._meta.params = { h24, min, sousType: 'digital_direct', distracteurs: dis };
            return payload;
        }
        // sens inverse : la lecture courante → l'affichage
        const bonne = `${pad2(h24)}:${pad2(min)}`;
        const candidats = [
            { valeur: `${pad2(h12)}:${pad2(min)}`, code: 'oubli_plus_12' },
            { valeur: `${pad2(h12 + 10)}:${pad2(min)}`, code: 'plus_10' },
            { valeur: `${pad2(h24 % 12 + 1)}:${pad2(min)}`, code: 'heure_voisine' },
        ];
        const vus = new Set([bonne]);
        const dis = candidats.filter((c) => {
            if (vus.has(c.valeur)) return false;
            vus.add(c.valeur);
            return true;
        }).slice(0, 3);
        if (dis.length < 3) continue;
        const payload = makeQcm(
            [bonne, ...dis.map((c) => c.valeur)],
            bonne,
            `<p><b>Il est ${h12} h ${pad2(min)} ${mo}. Qu'affiche la montre digitale ?</b></p>`,
            opts(L1),
            false,
        );
        payload.contenu = { ...payload.contenu, sousType: 'digital_inverse' };
        payload._meta.params = { h24, min, sousType: 'digital_inverse', distracteurs: dis };
        return payload;
    }
    const payload = makeQcm(
        ['6 h 05 du soir', '6 h 05 du matin', '8 h 05 du soir', '18 h 05 du matin'],
        '6 h 05 du soir',
        '<p><b>Comment dit-on l\'heure affichée ?</b></p><p>{{element:0}}</p>',
        { ...opts(L1), elements_additionnels: elImage(dataUrl(svgDigital(18, 5))) },
        false,
    );
    payload.contenu = { ...payload.contenu, sousType: 'digital_direct' };
    payload._meta.params = { h24: 18, min: 5, sousType: 'digital_direct', distracteurs: [] };
    return payload;
};

// ─── Format 4 : V/F (équivalences digitales + l'instrument) ───────────────

// Les paires JUMELLES sur les aiguilles (courte/longue → heures,
// grande/trotteuse → minutes) peuvent cohabiter dans un tirage (~23 % des
// cas). ARBITRAGE PROF (revue 2026-08-22) : conservé À DESSEIN — un élève
// qui se contredit sur la paire répond au hasard, c'est un signal utile.
const AFFIRMATIONS = [
    { t: '18:05, c\'est 6 h 05 du soir', v: true },
    { t: '15:30, c\'est 3 h 30 de l\'après-midi', v: true },
    { t: '20:15, c\'est 8 h 15 du soir', v: true },
    { t: '13:40, c\'est 1 h 40 de l\'après-midi', v: true },
    { t: '14:00, c\'est 4 h de l\'après-midi', v: false },
    { t: '17:45, c\'est 7 h 45 du soir', v: false },
    { t: '22:10, c\'est 10 h 10 du matin', v: false },
    { t: 'L\'aiguille la plus courte indique les heures.', v: true },
    { t: 'La grande aiguille indique les minutes.', v: true },
    { t: 'La trotteuse indique les minutes.', v: false },
    { t: 'L\'aiguille la plus longue indique les heures.', v: false },
];

const genVraiFaux = () => {
    for (let essai = 0; essai < 30; essai++) {
        const tirage = shuffle([...AFFIRMATIONS]).slice(0, 4);
        const nbVraies = tirage.filter((a) => a.v).length;
        if (nbVraies < 1 || nbVraies > 3) continue;
        return makeVraiFaux(
            tirage.map((a) => a.t),
            tirage.map((a) => (a.v ? 0 : 1)),
            '<p><b>Vrai ou faux ?</b></p>',
            opts(L1),
        );
    }
    return makeVraiFaux(
        [AFFIRMATIONS[0].t, AFFIRMATIONS[4].t, AFFIRMATIONS[7].t, AFFIRMATIONS[9].t],
        [0, 1, 0, 1],
        '<p><b>Vrai ou faux ?</b></p>',
        opts(L1),
    );
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'PÉRIMÈTRE STRICT : lire une heure affichée — placer → 1032, unités → 1033, quart d\'heure → 1035',
        'la petite aiguille est TOUJOURS décalée de min × 0,5° (gardé par un throw)',
        '~60 % des cadrans dans la zone du piège (min ∈ [35 ; 55])',
        'minutes et secondes multiples de 5',
    ],
    lecture_cadran_qcm: {
        support: 'un cadran SVG + le MOMENT dans l\'énoncé (le matin / l\'après-midi / le soir, prof 2026-08-22) — réponse en heure sur 24 (« 15 h 50 » l\'après-midi)',
        fixes: {
            heure_suivante: 'l\'heure qui approche au lieu de l\'heure commencée — L\'ERREUR CIBLE',
            mauvais_moment: 'le moment ignoré (3 h 50 l\'après-midi) ou ajouté à tort (15 h 50 le matin)',
            aiguilles_echangees: 'l\'heure lue sur la grande aiguille',
            chiffre_brut: 'la minute lue comme le chiffre pointé (grande aiguille sur le 10 → 10 min)',
            heure_precedente: 'repli quand un piège coïncide avec la bonne réponse',
        },
    },
    lecture_digital_qcm: {
        support: 'affichage 24 h ↔ lecture courante, deux sens (2/3 direct, 1/3 inverse)',
        fixes: {
            matin_apres_midi: 'le bon nombre, le mauvais moment de la journée',
            chiffre_des_unites: '18 lu « 8 » (le chiffre des unités)',
            vingt_quatre_du_matin: 'l\'heure 24 h recopiée avec « du matin »',
            oubli_plus_12: 'l\'affichage sans les 12 heures ajoutées',
            plus_10: '+10 au lieu de +12',
            heure_voisine: 'l\'heure d\'à côté',
        },
    },
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    lecture_cadran_qcm: genQcmCadran,
    lecture_cadran_saisie: genSaisieCadran,
    lecture_digital_qcm: genQcmDigital,
    lecture_tableau_vf: genVraiFaux,
};

const FORMAT_LABELS = {
    lecture_cadran_qcm: 'QCM cadran',
    lecture_cadran_saisie: 'Saisie cadran',
    lecture_digital_qcm: 'QCM digital',
    lecture_tableau_vf: 'Vrai/Faux',
};

export const CATEGORIES = [
    {
        id: 'lectureheure',
        label: L1,
        formatsList: Object.keys(GENERATORS).map((k) => ({ key: k, label: FORMAT_LABELS[k] || k })),
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
